import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { makeStyles } from '@material-ui/core/styles';
import { Link } from '@backstage/core-components';
import { SearchIcon } from '../../components/icons';

/*
 * The landing hero: panel-tier slab, yellow tick-rule, and the one search field
 * that covers every doc. The field submits to the search page pre-filtered to
 * TechDocs results rather than searching inline, so one result surface stays
 * authoritative.
 */
const useStyles = makeStyles(theme => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: 14,
    padding: '40px 44px',
    borderRadius: 16,
    background: 'var(--bui-bg-neutral-2)',
    // Phone: the slab keeps its shape on a tighter box.
    [theme.breakpoints.down('xs')]: {
      gap: 12,
      padding: '24px 20px',
    },
  },
  tick: {
    width: 32,
    height: 4,
    borderRadius: 2,
    background: '#f8f93f',
  },
  title: {
    margin: 0,
    fontSize: 28,
    fontWeight: 600,
    // The theme's tightened heading leading is for page titles; the hero sets
    // its own so the slab keeps the mock's height.
    lineHeight: 'normal',
    letterSpacing: '-0.025em',
    color: 'var(--bui-fg-primary)',
    [theme.breakpoints.down('xs')]: {
      fontSize: 22,
      letterSpacing: '-0.02em',
    },
  },
  subtitle: {
    margin: 0,
    maxWidth: 520,
    fontSize: 14,
    lineHeight: 1.6,
    color: 'var(--bui-fg-secondary)',
  },
  searchRow: {
    display: 'flex',
    alignItems: 'center',
    marginTop: 6,
  },
  search: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    boxSizing: 'border-box',
    width: 640,
    maxWidth: '100%',
    height: 48,
    padding: '0 16px',
    borderRadius: 12,
    background: 'var(--bui-white)',
    boxShadow: '0 0 0 1px rgba(0,0,0,0.1), 0 2px 6px rgba(0,0,0,0.05)',
    // The ring belongs to the rounded field, not the bare input inside it —
    // otherwise focus draws a squared-off rectangle within the pill.
    '&:focus-within': {
      boxShadow: '0 0 0 2px var(--bui-white), 0 0 0 4px #039cfb',
    },
    // Phone: the field is the hero's only control, so it takes the full box.
    [theme.breakpoints.down('xs')]: {
      width: '100%',
      height: 44,
      padding: '0 14px',
    },
  },
  searchIcon: {
    width: 16,
    height: 16,
    opacity: 0.5,
  },
  input: {
    flex: 1,
    minWidth: 0,
    padding: 0,
    border: 'none',
    outline: 'none',
    background: 'transparent',
    fontFamily: "'DM Sans', sans-serif",
    fontSize: 14,
    color: 'var(--bui-fg-primary)',
    '&:focus-visible': {
      outline: 'none',
      boxShadow: 'none',
    },
    '&::placeholder': {
      color: 'var(--hone-portal-fg-tertiary)',
    },
    // Chrome's own clear glyph on type=search does not belong in the field.
    '&::-webkit-search-cancel-button': {
      appearance: 'none',
    },
  },
  kbd: {
    padding: '1px 6px',
    border: '1px solid var(--bui-border-1)',
    borderRadius: 4,
    background: 'var(--bui-bg-neutral-2)',
    fontFamily: "'DM Mono', monospace",
    fontSize: 10.5,
    color: 'var(--bui-fg-secondary)',
    // A key hint has nothing to say on a touch keyboard.
    [theme.breakpoints.down('xs')]: {
      display: 'none',
    },
  },
  // Plain grey caption text, not a button — so it doesn't compete with the
  // pill above it or ever read as a second way to submit the search.
  treeLink: {
    margin: 0,
    paddingLeft: 16,
    fontSize: 12.5,
    color: 'var(--bui-fg-secondary)',
  },
  treeLinkAnchor: {
    fontWeight: 500,
    color: 'var(--bui-fg-secondary)',
    textDecoration: 'underline',
    textUnderlineOffset: 2,
    '&:hover': {
      color: 'var(--bui-fg-primary)',
    },
  },
}));

export const DocsHero = () => {
  const classes = useStyles();
  const navigate = useNavigate();
  const [term, setTerm] = useState('');

  const submit = (event: FormEvent) => {
    event.preventDefault();
    const query = term.trim();
    if (!query) return;
    navigate(`/search?query=${encodeURIComponent(query)}&types%5B%5D=techdocs`);
  };

  return (
    <section className={classes.root}>
      <span className={classes.tick} />
      <h1 className={classes.title}>Hone knowledge base</h1>
      <p className={classes.subtitle}>
        Handbooks, runbooks and standards for everything we run — searchable in
        one place, owned by the teams that ship it.
      </p>
      <div className={classes.searchRow}>
        <form className={classes.search} onSubmit={submit} role="search">
          <SearchIcon className={classes.searchIcon} />
          <input
            className={classes.input}
            type="search"
            value={term}
            onChange={event => setTerm(event.target.value)}
            placeholder="Search all documentation…"
            aria-label="Search all documentation"
          />
          <span className={classes.kbd}>⏎</span>
        </form>
      </div>
      <p className={classes.treeLink}>
        Prefer to browse?{' '}
        <Link className={classes.treeLinkAnchor} to="/search?tab=tree">
          See the full documentation tree
        </Link>
      </p>
    </section>
  );
};
